/**
 * Pill-shaped read-only chip used to render a single tag inline.
 *
 * Kept intentionally small (no hover state, no icon) so a row of 5-6
 * tags in a table cell stays readable. For the editable
 * multi-select widget see ``TagsField``; for the filter dropdown
 * shown on list pages see ``TagFilter``.
 */
import { cn } from "@/lib/cn";

export interface TagBadgeProps {
  tag: string;
  className?: string;
}

export function TagBadge({ tag, className }: TagBadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex h-5 items-center rounded-full bg-muted px-2 font-mono text-[11px] text-muted-foreground",
        className,
      )}
    >
      {tag}
    </span>
  );
}

export interface TagListProps {
  tags: string[];
  /** Hide tags after this many; show a ``+N`` overflow chip. */
  max?: number;
  className?: string;
}

export function TagList({ tags, max, className }: TagListProps) {
  if (!tags || tags.length === 0) return null;
  const visible = max ? tags.slice(0, max) : tags;
  const overflow = max ? Math.max(0, tags.length - max) : 0;
  return (
    <div className={cn("flex flex-wrap items-center gap-1", className)}>
      {visible.map((t) => (
        <TagBadge key={t} tag={t} />
      ))}
      {overflow > 0 && <TagBadge tag={`+${overflow}`} />}
    </div>
  );
}
